import { copyMediaAssetToStorageObjectPath } from './browserMedia';
import {
  LEVEL_DESIGN_IMAGE_ASSET_COLLECTION,
  markImageAssetDocumentMigrated,
  readImageAssetDocument,
} from './imageAssetDocuments';
import {
  createLevelDesignStorageObjectPath,
  inferImageContentType,
  inferImageExtension,
  isOwnedLevelDesignStorageObjectPath,
  isStorageObjectPath,
} from './mediaStoragePaths';
import {
  parseLevelDesignImageAssetPath,
  persistLevelDesignImageAsset,
  readLevelDesignImageAsset,
} from '../levelDesignImageAssets';

const resolveLegacyLevelDesignImagePath = (value: string): string => {
  const assetId = parseLevelDesignImageAssetPath(value);
  if (!assetId) {
    return value.trim();
  }

  return readLevelDesignImageAsset(assetId) ?? value.trim();
};

export const resolveLevelDesignImagePathAsync = async (value: string): Promise<string> => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  const resolved = resolveLegacyLevelDesignImagePath(trimmed);
  if (resolved !== trimmed) {
    return resolved.trim();
  }

  const assetId = parseLevelDesignImageAssetPath(trimmed);
  if (!assetId) {
    return trimmed;
  }

  const persisted = await readImageAssetDocument(LEVEL_DESIGN_IMAGE_ASSET_COLLECTION, assetId);
  if (!persisted?.trim()) {
    return trimmed;
  }

  persistLevelDesignImageAsset(assetId, persisted);
  return persisted.trim();
};

export const ensureLevelDesignImagePathStored = async ({
  assetId,
  designId,
  imagePath,
  userId,
}: {
  assetId: string;
  designId: string;
  imagePath: string;
  userId: string;
}): Promise<string> => {
  const originalPath = imagePath.trim();
  const resolvedPath =
    (await resolveLevelDesignImagePathAsync(originalPath)).trim() || originalPath;
  if (!resolvedPath) {
    return '';
  }

  if (
    isStorageObjectPath(resolvedPath) &&
    isOwnedLevelDesignStorageObjectPath(resolvedPath, userId, designId)
  ) {
    return resolvedPath;
  }

  const extension = inferImageExtension({
    fileName: resolvedPath,
    contentType: inferImageContentType(resolvedPath),
    fallback: resolvedPath.toLowerCase().endsWith('.gif') ? 'gif' : 'png',
  });
  const targetPath = createLevelDesignStorageObjectPath(userId, designId, assetId, extension);

  if (resolvedPath !== targetPath) {
    await copyMediaAssetToStorageObjectPath(resolvedPath, targetPath, {
      contentType: inferImageContentType(resolvedPath) ?? undefined,
    });
  }

  const legacyAssetId = parseLevelDesignImageAssetPath(originalPath);
  if (legacyAssetId) {
    await markImageAssetDocumentMigrated(
      LEVEL_DESIGN_IMAGE_ASSET_COLLECTION,
      legacyAssetId,
      targetPath,
    );
  }

  return targetPath;
};
